/* * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights
Reserved. * * Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with * the License. A copy of the
License is located at * * http://aws.amazon.com/apache2.0/ * * or in the
"license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR * CONDITIONS OF ANY KIND, either express or
implied. See the License for the specific language governing permissions * and
limitations under the License. */

<template>
  <div class="confirm-signup-container">
    <validation-observer ref="obs" v-slot="ObserverProps">
      <div class="logo"><img src="@/assets/logo.png" /></div>
      <div v-if="!options.hasRegisteredEmail" class="form-input">
        <div class="label">メールアドレス</div>
        <ValidationProvider rules="required" immediate v-slot="ProviderProps">
          <input v-model="email" type="text" autofocus />
          <div class="validation-error">
            {{ ProviderProps.errors[0] }}
          </div>
        </ValidationProvider>
      </div>
      <div class="form-input">
        <div>
          <p v-if="options.afterSignUp">
            入力したメールアドレスに確認のメールを送信しました。
          </p>
          <p>
            受信したメールに記載されている検証コードを入力してください。
          </p>
        </div>
        <ValidationProvider rules="required" immediate v-slot="ProviderProps">
          <input v-model="code" type="text" autofocus />
          <div class="validation-error">
            {{ ProviderProps.errors[0] }}
          </div>
        </ValidationProvider>
      </div>
      <div class="actions" :class="{ disable: sending }">
        <div class="nav">
          <div>
            <a v-on:click="back">戻る</a>
          </div>
        </div>
        <div>
          <div class="submit">
            <span
              class="signup-button"
              :class="{ disable: ObserverProps.invalid }"
              v-on:click="confirm"
              ><span class="arrow-right"></span>送信</span
            >
          </div>
        </div>
      </div>
      <div class="btn-resend">
        <span @click="resend">メールアドレスに検証コードを再送信</span>
      </div>
      <div class="error" v-if="error">
        {{ error }}
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { AmplifyEventBus } from "aws-amplify-vue";
import Loader from "@/mixins/loader";
import ErrorMessage from "@/mixins/errormessages";
import AccountInitializer from "@/mixins/account-initializer";

export default {
  name: "ConfirmSignUp",
  mixins: [Loader, ErrorMessage, AccountInitializer],
  props: ["confirmSignUpConfig", "usernameAttributes"],
  data() {
    return {
      email: "",
      code: "",
      error: "",
      logger: {},
      sending: false,
    };
  },
  computed: {
    options() {
      const defaults = {
        username: "",
        header: this.$Amplify.I18n.get("Confirm Sign Up"),
      };
      return Object.assign(defaults, this.confirmSignUpConfig || {});
    },
  },
  mounted: function() {
    this.logger = new this.$Amplify.Logger(this.$options.name);
    this.email = this.options.username;
  },
  methods: {
    async confirm() {
      this.loading(async () => {
        try {
          await this.$Amplify.Auth.confirmSignUp(this.email, this.code);
          this.logger.info("confirmSignUp success");

          // パスワードが無い場合はログイン画面に遷移
          if (!this.options.password) {
            this.$toasted.success(
              "アカウント登録しました。ログインしてください。",
              {
                position: "top-center",
                duration: 3000,
              }
            );
            AmplifyEventBus.$emit("localUser", {
              username: this.email,
            });
            return AmplifyEventBus.$emit("authState", "signIn");
          }

          // メールアドレスの検証に成功したらアカウントを初期化する
          await this.initializeAccount(this.email, this.options.password);

          // 登録完了画面へ遷移
          return AmplifyEventBus.$emit("authState", "signUpComplete");
        } catch (error) {
          this.setError(error);
        }
      });
    },
    resend() {
      if (this.email) {
        this.$Amplify.Auth.resendSignUp(this.email)
          .then(() => {
            this.logger.info("resendSignUp success");
            this.$toasted.success("検証コードを送信しました。", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((e) => this.setError(e));
      }
    },
    back() {
      // サインイン画面に戻る
      AmplifyEventBus.$emit("authState", "signIn");
    },
    setError(e) {
      this.error = this.convertSignUpErrorMessage(e);
      this.logger.error(this.error);
    },
  },
};
</script>

<style lang="scss">
.confirm-signup-container {
  width: 100vw;
  background: #ffffff;
  padding: 3rem;
  color: var(--purple);

  .desktop & {
    width: 552px;
    box-shadow: 0px 0px 20px #00000029;
    border-radius: 25px;
  }

  .logo {
    height: 3rem;
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;

    img {
      object-fit: contain;
    }
  }

  .form-input {
    margin-bottom: 1rem;

    .label {
      margin-bottom: 0.5rem;
    }

    input[type="text"],
    input[type="password"],
    input[type="date"] {
      width: 100%;
      height: 40px;
      border-radius: 20px;
      border: solid 1px var(--purple);
      padding: 0 16px;
      font-size: 16px;
      outline: none;
    }

    input[type="text"]:focus,
    input[type="password"]:focus,
    input[type="date"]:focus {
      background: var(--purple-tertiary);
    }
  }

  .notes {
    margin-top: 2rem;
    font-size: 0.8rem;
  }

  .actions {
    margin-top: 3rem;
    display: flex;
    align-items: center;

    .desktop & {
      margin-top: 2rem;
      justify-content: space-between;
    }

    &.disable {
      cursor: none;
      pointer-events: none;
      opacity: 0.5;
    }

    .nav {
      display: flex;
      justify-content: start;
      align-items: center;
      color: #1a82b7;
      cursor: pointer;

      .mobile & {
        margin-top: 3rem;
      }

      a:active {
        opacity: 0.5;
      }
    }

    .submit {
      .signup-button {
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--purple);
        width: 140px;
        height: 44px;
        border-radius: 22px;
        color: #fff;
        cursor: pointer;

        &:active {
          opacity: 0.7;
        }
        &.disable {
          opacity: 0.5;
          cursor: inherit;
          pointer-events: none;
        }
      }
    }
  }
  .btn-resend {
    display: flex;
    justify-content: end;
    span {
      display: inline-block;
      background: white;
      font-size: 14px;
      text-align: center;
      height: 30px;
      border-radius: 15px;
      color: var(--purple);
      padding: 6px 20px;
      margin-top: 2rem;
      cursor: pointer;

      background: var(--purple-tertiary);
      color: #000;
    }
  }
}
</style>
